import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Text from "./Text";

const RichTextEditor = ({
  onChange,
  value = "",
  placeholder,
  className,
  id = "rich-editor",
}) => {
  const [imageSrc, setImageSrc] = useState("");
  const [tableCols, setTableCols] = useState(1);
  const [tableRows, setTableRows] = useState(1);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkDisplay, setLinkDisplay] = useState("");
  const [innerValue, setInnerValue] = useState(value);
  const refRichTextEditor = useRef();
  const refPlaceholder = useRef();
  const refTextArea = useRef();
  const firstRenderRef = useRef(true);

  useEffect(() => {
    if (value.trim().length > 0) {
      refPlaceholder.current.style.display = "none";
    } else {
      refPlaceholder.current.style.display = "block";
    }
    if (firstRenderRef.current || value === "" || value !== innerValue) {
      refTextArea.current.innerHTML = value;
      firstRenderRef.current = false;
    }
  });

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.innerHTML);
    }
    setInnerValue(e.target.innerHTML);
  };

  const insertFormat = (newFormat, params) => {
    const sel = window.getSelection();
    const node = sel.baseNode;

    if (isDescendant(refRichTextEditor.current, node)) {
      const parentNode = sel.anchorNode.parentNode.nodeName;
      switch (newFormat) {
        case "BOLD": {
          document.execCommand("bold");
          break;
        }
        case "ITALIC": {
          document.execCommand("italic");
          break;
        }
        case "STRIKE_TRHOUGH": {
          document.execCommand("strikeThrough");
          break;
        }
        case "UNDERLINE": {
          document.execCommand("underline");
          break;
        }
        case "ORDERED_LIST": {
          document.execCommand("insertOrderedList");
          break;
        }
        case "UNORDERED_LIST": {
          document.execCommand("insertUnorderedList");
          break;
        }
        case "TABLE": {
          let tableStructure = ``;
          for (let row = 0; row < params.rows; row++) {
            let colsStructure = ``;
            for (let col = 0; col < params.cols; col++) {
              colsStructure += `<td></td>`;
            }
            tableStructure += `<tr>${colsStructure}</tr>`;
          }
          document.execCommand(
            "insertHTML",
            false,
            `<table border="1">${tableStructure}</table>`
          );
          break;
        }
        case "LINK": {
          document.execCommand(
            "insertHTML",
            false,
            `<a href="${params.url}" target="${params.target || ""}">${
              params.display || params.url
            }</table>`
          );
          break;
        }
        case "IMAGE": {
          if (/(https?:\/\/.*\.(?:png|jpg|gif|svg))/.test(imageSrc)) {
            document.execCommand("insertImage", false, imageSrc);
            setImageSrc("");
          }

          break;
        }
        case "JUSTIFY_LEFT": {
          document.execCommand("justifyLeft");
          break;
        }
        case "JUSTIFY_CENTER": {
          document.execCommand("justifyCenter");
          break;
        }
        case "JUSTIFY_RIGHT": {
          document.execCommand("justifyRight");
          break;
        }
        case "JUSTIFY_FULL": {
          document.execCommand("justifyFull");
          break;
        }

        default: {
          if (parentNode !== newFormat) {
            document.execCommand("formatBlock", false, newFormat);
          } else {
            document.execCommand("formatBlock", false, "DIV");
          }
        }
      }
    }
  };

  const hideToggle = (id) => {
    const element = document.getElementById(id);
    if (Object.values(element.classList).includes("hidden")) {
      element.classList.remove("hidden");
    } else {
      element.classList.add("hidden");
    }
  };

  const handleInserFormat = (e, formatParams) => {
    e.preventDefault();
    const { newFormat, params } = formatParams;
    insertFormat(newFormat, params);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 9) {
      const sel = window.getSelection();
      const elementNode = sel.anchorNode.nodeName;
      const parentNode = sel.anchorNode.parentNode.nodeName;
      e.preventDefault();
      if (elementNode === "LI" || parentNode === "LI") {
        document.execCommand("indent", false, "");
      } else {
        document.execCommand("insertHTML", false, "&nbsp;&nbsp;");
      }
    }
  };

  return (
    <>
      <RichTextEditorContainer ref={refRichTextEditor}>
        <div className="controls">
          <button
            className="btn-format"
            onClick={(e) => {
              e.preventDefault();
              hideToggle(`${id}-header-levels`);
            }}
          >
            <i className="fas fa-heading"></i>
          </button>
          <div
            id={`${id}-header-levels`}
            className="subcontrols header-levels hidden"
          >
            <button
              className="btn-format"
              onClick={(e) => {
                handleInserFormat(e, { newFormat: `H${2}` });
              }}
            >
              2
            </button>
            <button
              className="btn-format"
              onClick={(e) => {
                handleInserFormat(e, { newFormat: `H${3}` });
              }}
            >
              3
            </button>
            <button
              className="btn-format"
              onClick={(e) => {
                handleInserFormat(e, { newFormat: `H${4}` });
              }}
            >
              4
            </button>
            <button
              className="btn-format"
              onClick={(e) => {
                handleInserFormat(e, { newFormat: `H${5}` });
              }}
            >
              5
            </button>
            <button
              className="btn-format"
              onClick={(e) => {
                handleInserFormat(e, { newFormat: `H${6}` });
              }}
            >
              6
            </button>
          </div>

          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "BOLD" });
            }}
          >
            <i className="fas fa-bold"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "ITALIC" });
            }}
          >
            <i className="fas fa-italic"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "STRIKE_TRHOUGH" });
            }}
          >
            <i className="fas fa-strikethrough"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "UNDERLINE" });
            }}
          >
            <i className="fas fa-underline"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "ORDERED_LIST" });
            }}
          >
            <i className="fas fa-list-ol"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "UNORDERED_LIST" });
            }}
          >
            <i className="fas fa-list-ul"></i>
          </button>

          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "JUSTIFY_LEFT" });
            }}
          >
            <i className="fas fa-align-left"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "JUSTIFY_CENTER" });
            }}
          >
            <i className="fas fa-align-center"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "JUSTIFY_RIGHT" });
            }}
          >
            <i className="fas fa-align-right"></i>
          </button>

          <button
            className="btn-format"
            onClick={(e) => {
              handleInserFormat(e, { newFormat: "JUSTIFY_FULL" });
            }}
          >
            <i className="fas fa-align-justify"></i>
          </button>
          <button
            className="btn-format"
            onClick={(e) => {
              e.preventDefault();
              hideToggle(`${id}-table-dimensions`);
            }}
          >
            <i className="fas fa-table"></i>
          </button>

          <div
            id={`${id}-table-dimensions`}
            className="subcontrols table-dimensions hidden"
          >
            <label htmlFor="rows" className="rich-label">rows</label>
            <Text
              type="number"
              onChange={setTableRows}
              value={tableRows}
              placeholder="0"
              name="rows"
              min={1}
            />
            <label htmlFor="cols" className="rich-label">cols</label>
            <Text
              type="number"
              onChange={setTableCols}
              value={tableCols}
              placeholder="0"
              name="cols"
              min={1}
            />
            <button
              onClick={(e) => {
                handleInserFormat(e, {
                  newFormat: "TABLE",
                  params: { rows: tableRows, cols: tableCols },
                });
              }}
            >
              Ok
            </button>
          </div>
          <button
            className="btn-format"
            onClick={(e) => {
              e.preventDefault();
              hideToggle(`${id}-img-src`);
            }}
          >
            <i className="far fa-image"></i>
          </button>

          <div id={`${id}-img-src`} className="subcontrols img-src hidden">
            <Text
              onChange={setImageSrc}
              value={imageSrc}
              placeholder="Image src"
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                if (
                  window.getSelection().baseNode ===
                    document.getElementById(`${id}-textarea-input`) ||
                  isDescendant(
                    document.getElementById(`${id}-textarea-input`),
                    window.getSelection().baseNode
                  )
                ) {
                  handleInserFormat(e, { newFormat: "IMAGE" });
                }
              }}
            >
              Ok
            </button>
          </div>

          <button
            className="btn-format"
            onClick={(e) => {
              e.preventDefault();
              hideToggle(`${id}-link-params`);
            }}
          >
            <i className="fas fa-link"></i>
          </button>

          <div
            id={`${id}-link-params`}
            className="subcontrols link-params hidden"
          >
            <Text placeholder="url" value={linkUrl} onChange={setLinkUrl} />
            <Text
              placeholder="display"
              value={linkDisplay}
              onChange={setLinkDisplay}
            />
            <button
              onClick={(e) => {
                handleInserFormat(e, {
                  newFormat: "LINK",
                  params: {
                    url: linkUrl,
                    display: linkDisplay,
                  },
                });
              }}
            >
              Ok
            </button>
          </div>
        </div>

        <div className={`editor-container ${className & className}`}>
          <span
            id={`placeholder ${className & `${className}-placeholder`}`}
            ref={refPlaceholder}
            className="placeholder"
          >
            {placeholder}
          </span>

          <div
            type="text"
            id={`${id}-textarea-input`}
            onInput={handleChange}
            contentEditable
            suppressContentEditableWarning={true}
            className={`textarea ${className & `${className}-textarea`}`}
            ref={refTextArea}
            onKeyDown={handleKeyDown}
          ></div>
        </div>
      </RichTextEditorContainer>
    </>
  );
};

const RichTextEditorContainer = styled.div`
  .editor-container {
    .placeholder,
    .textarea {
      padding: 1rem;
    }
    .placeholder {
      margin-bottom: -4.5rem;
      color: lightgray;
      border: 1px solid transparent;
    }
    .textarea {
      padding: 1rem;
      width: 100%;
      min-height: 1rem;
      max-height: 40rem;
      border: 1px solid black;
      resize: vertical;
      overflow-x: auto;
      overflow-y: scroll;
    }
  }
  .controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    .btn-format {
      display: inline-flex;
      font-size: 2rem;
      height: 3rem;
      width: 3rem;
    }
    .subcontrols {
      display: flex;
      height: 4rem;
      border: 0.2rem solid var(--mainColor);
      border-radius: 0.5rem;
      padding: 0.3rem;
      margin: 0 0.3rem;
      button {
        margin: 0.1rem;
        padding: 0;
      }
      input[type="text"] {
        margin: 0.1rem;
        padding: 0.1rem;
      }
      .rich-label{
        min-width: 4rem;
      }
    }
    .hidden {
      display: none;
    }
  }
`;

export default RichTextEditor;

const isDescendant = (parent, child) => {
  let node = child.parentNode;
  while (node !== null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

// const haveAncester = (node, ancesterName, stopNode) => {
//   let node = child.parentNode;
//   while (node !== stopNode) {
//     if (node.nodeName === ancesterName) {
//       return true;
//     }
//     node = node.parentNode;
//   }
//   return false;
// }